<template>
  <div class="bg-white rounded-lg shadow p-4">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-700">국고채</h3>
      <!-- 전체선택/해제 체크박스 -->
      <label class="flex items-center space-x-2 cursor-pointer">
        <input type="checkbox" :checked="isAllTypesSelected" @change="toggleAllTypes"
          class="rounded text-blue-600 w-4 h-4" />
        <span class="text-xs text-gray-600">전체선택</span>
      </label>
    </div>

    <!-- 3열 그리드 버튼 UI -->
    <div class="grid grid-cols-3 gap-2 mb-4">
      <div v-for="type in benchmarkTypes" :key="type.id" class="relative">
        <div class="flex">
          <button @click="selectMainType(type)" :class="[
            'flex-1 px-2.5 py-1.5 rounded-l-md text-sm font-medium transition-colors',
            isActiveMainType(type.id)
              ? 'bg-gray-800 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          ]">
            {{ type.name }}
          </button>
          <button @click="toggleDropdown(type.id)" :class="[
            'px-1 rounded-r-md text-sm font-medium transition-colors border-l',
            isActiveMainType(type.id)
              ? 'bg-gray-800 text-white border-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300',
          ]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" :class="{ 'transform rotate-180': openDropdowns[type.id] }">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <!-- 드롭다운 메뉴 -->
        <div v-if="openDropdowns[type.id]" class="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
          <!-- 전체 선택/해제 -->
          <div class="p-2 border-b">
            <label class="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" :checked="isAllSubtypesSelected(type.id)" @change="toggleAllSubtypes(type.id)"
                class="rounded text-blue-600" />
              <span class="text-xs">전체 선택</span>
            </label>
          </div>

          <!-- 개별 선택 옵션 -->
          <div class="max-h-48 overflow-y-auto py-1">
            <div v-for="bond in bondsByType[type.id] || []" :key="bond.code" class="px-2 py-1 hover:bg-gray-50">
              <label class="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" :checked="isSelected(bond.code)" @change="toggleBond(type.id, bond)"
                  class="rounded text-blue-600" />
                <span class="text-xs">{{ bond.alias }}</span>
              </label>
            </div>
            <div v-if="!bondsByType[type.id] || bondsByType[type.id].length === 0"
              class="px-2 py-1 text-xs text-gray-500 text-center">
              로딩 중...
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import ktbService from '@/service/ktbService';

export default {
  name: 'KtbTypeComponent',
  props: {
    modelValue: {
      type: [String, Array, Object],
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const benchmarkTypes = [
      { id: 'Y01', name: '연내' },
      { id: 'Y02', name: '2년' },
      { id: 'Y03', name: '3년' },
      { id: 'Y05', name: '5년' },
      { id: 'Y10', name: '10년' },
      { id: 'Y20', name: '20년' },
      { id: 'Y30', name: '30년' },
    ];

    // 상태 변수들
    const openDropdowns = ref({});
    const selectedBonds = ref([]);
    const activeTypes = ref(
      Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue]
    );
    const bondsByType = ref({});
    const isLoading = ref(false);

    // 드롭다운 초기화
    benchmarkTypes.forEach(type => {
      openDropdowns.value[type.id] = false;
    });

    // 본드 데이터 로드
    const loadBonds = async () => {
      try {
        isLoading.value = true;
        const data = await ktbService.findAllKtbBonds();

        // 새로운 API 응답을 maturityRangeType별로 그룹화
        const groupedData = {};
        data.forEach(bond => {
          const type = bond.maturityRangeType;
          if (!groupedData[type]) {
            groupedData[type] = [];
          }
          groupedData[type].push({
            code: bond.code,
            bondName: bond.alias, // alias를 bondName으로 사용
            alias: bond.alias,
            maturityRangeType: bond.maturityRangeType,
            maturityTypeName: bond.maturityTypeName,
            isBenchmark: bond.isBenchmark
          });
        });

        bondsByType.value = groupedData;

        // 초기 로드 후, 활성화된 타입의 채권들을 전체 선택
        updateSelectedBondsForActiveTypes();
      } catch (error) {
        console.error('Failed to load bond data:', error);
      } finally {
        isLoading.value = false;
      }
    };

    // 활성화된 타입의 모든 채권 선택 업데이트
    const updateSelectedBondsForActiveTypes = () => {
      activeTypes.value.forEach(typeId => {
        const bonds = bondsByType.value[typeId] || [];
        bonds.forEach(bond => {
          if (!selectedBonds.value.includes(bond.code)) {
            selectedBonds.value.push(bond.code);
          }
        });
      });
      emitUpdate();
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadBonds();
    });

    // 드롭다운 토글
    const toggleDropdown = typeId => {
      openDropdowns.value[typeId] = !openDropdowns.value[typeId];

      // 다른 드롭다운 닫기
      Object.keys(openDropdowns.value).forEach(key => {
        if (key !== typeId) {
          openDropdowns.value[key] = false;
        }
      });
    };

    // 외부 클릭 감지 이벤트 핸들러
    const handleClickOutside = event => {
      const dropdownContainer = event.target.closest('.relative');
      if (!dropdownContainer) {
        // 모든 드롭다운 닫기
        Object.keys(openDropdowns.value).forEach(key => {
          openDropdowns.value[key] = false;
        });
      }
    };

    // 메인 타입 선택 (버튼 클릭)
    const selectMainType = type => {
      // 드롭다운이 열려있으면 선택 상태를 변경하지 않고 드롭다운만 닫기
      if (openDropdowns.value[type.id]) {
        openDropdowns.value[type.id] = false;
        return;
      }

      const index = activeTypes.value.indexOf(type.id);

      if (index === -1) {
        // 현재 선택되지 않은 경우 추가
        activeTypes.value.push(type.id);

        // 해당 타입의 모든 채권 선택
        const bonds = bondsByType.value[type.id] || [];
        bonds.forEach(bond => {
          if (!selectedBonds.value.includes(bond.code)) {
            selectedBonds.value.push(bond.code);
          }
        });
      } else {
        // 이미 선택된 경우 제거
        activeTypes.value.splice(index, 1);

        // 해당 타입의 모든 채권 선택 해제
        const bondsToRemove = (bondsByType.value[type.id] || []).map(
          bond => bond.code
        );
        selectedBonds.value = selectedBonds.value.filter(
          code => !bondsToRemove.includes(code)
        );
      }

      emitUpdate();
    };

    // 메인 타입이 활성화되었는지 확인
    const isActiveMainType = typeId => {
      return activeTypes.value.includes(typeId);
    };

    // 특정 타입의 모든 하위 항목이 선택되었는지 확인
    const isAllSubtypesSelected = typeId => {
      const bonds = bondsByType.value[typeId] || [];
      if (bonds.length === 0) return false;

      return bonds.every(bond => isSelected(bond.code));
    };

    // 특정 타입의 모든 하위 항목 선택/해제
    const toggleAllSubtypes = typeId => {
      const bonds = bondsByType.value[typeId] || [];
      const allSelected = isAllSubtypesSelected(typeId);

      if (allSelected) {
        // 모두 선택된 경우 모두 해제
        bonds.forEach(bond => {
          const index = selectedBonds.value.indexOf(bond.code);
          if (index !== -1) {
            selectedBonds.value.splice(index, 1);
          }
        });

        // 메인 타입도 해제
        const typeIndex = activeTypes.value.indexOf(typeId);
        if (typeIndex !== -1) {
          activeTypes.value.splice(typeIndex, 1);
        }
      } else {
        // 일부만 선택된 경우 모두 선택
        bonds.forEach(bond => {
          if (!isSelected(bond.code)) {
            selectedBonds.value.push(bond.code);
          }
        });

        // 메인 타입 선택
        if (!activeTypes.value.includes(typeId)) {
          activeTypes.value.push(typeId);
        }
      }

      emitUpdate();
    };

    // 개별 본드 선택/해제
    const toggleBond = (typeId, bond) => {
      const index = selectedBonds.value.indexOf(bond.code);

      if (index === -1) {
        // 선택되지 않은 경우 추가
        selectedBonds.value.push(bond.code);

        // 이 타입이 아직 활성화되지 않았다면 활성화
        if (!activeTypes.value.includes(typeId)) {
          activeTypes.value.push(typeId);
        }
      } else {
        // 이미 선택된 경우 제거
        selectedBonds.value.splice(index, 1);

        // 해당 타입의 모든 본드가 선택 해제되었다면 메인 타입도 해제
        const typeHasSelectedBonds = selectedBonds.value.some(code => {
          const selectedBond = findBondByCode(code);
          return selectedBond && findTypeByBond(selectedBond) === typeId;
        });

        if (!typeHasSelectedBonds && activeTypes.value.includes(typeId)) {
          const typeIndex = activeTypes.value.indexOf(typeId);
          activeTypes.value.splice(typeIndex, 1);
        }
      }

      emitUpdate();
    };

    // 코드로 본드 찾기
    const findBondByCode = code => {
      for (const typeId in bondsByType.value) {
        const found = bondsByType.value[typeId]?.find(
          bond => bond.code === code
        );
        if (found) return found;
      }
      return null;
    };

    // 본드가 속한 타입 ID 찾기
    const findTypeByBond = bond => {
      for (const typeId in bondsByType.value) {
        if (bondsByType.value[typeId]?.some(b => b.code === bond.code)) {
          return typeId;
        }
      }
      return null;
    };

    // 본드가 선택되었는지 확인
    const isSelected = bondCode => {
      return selectedBonds.value.includes(bondCode);
    };

    // 본드 이름 포맷 (새로운 API에서는 alias를 직접 사용하므로 단순화)
    const formatBondName = bondName => {
      return bondName;
    };

    // 모든 타입이 선택되었는지 확인
    const isAllTypesSelected = computed(() => {
      return benchmarkTypes.length > 0 && benchmarkTypes.every(type =>
        activeTypes.value.includes(type.id)
      );
    });

    // 전체 타입 선택/해제
    const toggleAllTypes = () => {
      if (isAllTypesSelected.value) {
        // 전체 해제
        activeTypes.value = [];
        selectedBonds.value = [];
      } else {
        // 전체 선택
        activeTypes.value = benchmarkTypes.map(type => type.id);
        selectedBonds.value = [];

        // 모든 타입의 모든 채권 선택
        benchmarkTypes.forEach(type => {
          const bonds = bondsByType.value[type.id] || [];
          bonds.forEach(bond => {
            if (!selectedBonds.value.includes(bond.code)) {
              selectedBonds.value.push(bond.code);
            }
          });
        });
      }

      emitUpdate();
    };

    // 모델값 업데이트
    const emitUpdate = () => {
      // 메인 타입만 선택된 경우 (구체적인 본드가 선택되지 않은 경우)
      if (selectedBonds.value.length === 0) {
        emit(
          'update:modelValue',
          activeTypes.value.length === 1
            ? activeTypes.value[0]
            : activeTypes.value
        );
        return;
      }

      // 선택된 본드가 있는 경우
      const result = {
        types: [...activeTypes.value],
        bonds: [...selectedBonds.value],
      };

      emit('update:modelValue', result);
    };

    // 외부 클릭 감지 이벤트 등록 및 제거
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    // props 변경 감지
    watch(
      () => props.modelValue,
      newValue => {
        if (typeof newValue === 'string') {
          activeTypes.value = [newValue];
          selectedBonds.value = [];

          // 본드 데이터가 로드된 상태에서만 채권 선택 업데이트
          if (Object.keys(bondsByType.value).length > 0) {
            updateSelectedBondsForActiveTypes();
          }
        } else if (Array.isArray(newValue)) {
          activeTypes.value = newValue;
          selectedBonds.value = [];

          // 본드 데이터가 로드된 상태에서만 채권 선택 업데이트
          if (Object.keys(bondsByType.value).length > 0) {
            updateSelectedBondsForActiveTypes();
          }
        } else if (newValue && typeof newValue === 'object') {
          activeTypes.value = newValue.types || [];
          selectedBonds.value = newValue.bonds || [];
        }
      },
      { immediate: true }
    );

    // 본드 데이터 변경 감지
    watch(
      () => bondsByType.value,
      () => {
        if (Object.keys(bondsByType.value).length > 0) {
          updateSelectedBondsForActiveTypes();
        }
      }
    );

    return {
      benchmarkTypes,
      openDropdowns,
      bondsByType,
      selectedBonds,
      activeTypes,
      toggleDropdown,
      selectMainType,
      isActiveMainType,
      isAllSubtypesSelected,
      toggleAllSubtypes,
      toggleBond,
      isSelected,
      formatBondName,
      isAllTypesSelected,
      toggleAllTypes,
    };
  },
};
</script>

<style scoped>
.dropdown-content {
  position: absolute;
  z-index: 10;
  width: 100%;
}
</style>
